import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Shield, Users, Camera, Download, LogOut, Search, Eye, Loader2, MapPin, List, X, RefreshCw, UserPlus, Trash2, Key, UserCog } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAllRegistrations, exportRegistrations, getMapMarkers, updateRegistration, getAllAdmins, createAdmin, deleteAdmin, changeAdminPassword } from '../services/api';
import { useToast } from '../hooks/use-toast';
import MapView from '../components/MapView';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const { toast } = useToast();
  
  // View state
  const [activeView, setActiveView] = useState('table'); // 'table', 'map', or 'admins'
  
  // Data state
  const [registrations, setRegistrations] = useState([]);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [latDraft, setLatDraft] = useState('');
  const [lngDraft, setLngDraft] = useState('');
  const [isSavingCoords, setIsSavingCoords] = useState(false);
  // Synchronous in-flight guard (checked/set before any await so repeated
  // calls send only one PATCH) and a session token + record id that reflect
  // the CURRENTLY open dialog. A delayed response compares against these
  // refs, not values captured in the async closure, so it cannot overwrite
  // a different dialog/session.
  const saveInFlightRef = useRef(false);
  const detailsSessionRef = useRef(0);
  const openRecordIdRef = useRef(null);
  const formatCoord = (value) => (value === 0 ? '0' : String(value));

  // Every details request goes through here: resolve the record, seed the
  // coordinate drafts from it (zero stays "0"), and open the dialog. Bumping
  // the session token invalidates any in-flight save for a prior record.
  const openDetailsFor = (registration) => {
    if (saveInFlightRef.current) return; // do not switch records during a save
    const id = registration ? registration.id : null;
    openRecordIdRef.current = id;
    detailsSessionRef.current += 1;
    setSelectedUser(registration);
    setMapDetailsNotFound(!registration);
    setLatDraft(registration && registration.latitude != null && Number.isFinite(registration.latitude) ? formatCoord(registration.latitude) : '');
    setLngDraft(registration && registration.longitude != null && Number.isFinite(registration.longitude) ? formatCoord(registration.longitude) : '');
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    if (saveInFlightRef.current) return; // block close (X/Escape/overlay) during a save
    detailsSessionRef.current += 1; // invalidate any in-flight save
    openRecordIdRef.current = null;
    setDetailsOpen(false);
    setSelectedUser(null);
    setMapDetailsNotFound(false);
    setLatDraft('');
    setLngDraft('');
  };
  const [admins, setAdmins] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [isLoadingMarkers, setIsLoadingMarkers] = useState(false);
  const [mapDetailsNotFound, setMapDetailsNotFound] = useState(false);
  
  // Admin Management state
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [deletingAdminId, setDeletingAdminId] = useState(null);
  const [changingPasswordId, setChangingPasswordId] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [confirmNewPasswordValue, setConfirmNewPasswordValue] = useState('');
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  const [municipalityFilter, setMunicipalityFilter] = useState('all');
  const [detachmentFilter, setDetachmentFilter] = useState('all');

  // Status options
  const statusOptions = [
    { value: 'new', label: t.statusNew, color: 'bg-blue-100 text-blue-800' },
    { value: 'reviewed', label: t.statusReviewed, color: 'bg-orange-100 text-orange-800' },
    { value: 'active', label: t.statusActive, color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: t.statusInactive, color: 'bg-gray-100 text-gray-800' },
  ];
  
  // Property type options
  const propertyTypeOptions = [
    { value: 'residential', label: t.residential },
    { value: 'commercial', label: t.commercial },
    { value: 'farm', label: t.farm },
    { value: 'acreage', label: t.acreage },
  ];

  useEffect(() => {
    // Check if admin is logged in
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const token = localStorage.getItem('adminToken');
    
    if (!isLoggedIn || !token) {
      navigate('/admin');
      return;
    }
    
    // Fetch registrations
    fetchRegistrations();
  }, [navigate]);
  
  // Fetch map markers when switching to map view
  useEffect(() => {
    if (activeView === 'map') {
      fetchMapMarkers();
    }
    if (activeView === 'admins') {
      fetchAdmins();
    }
  }, [activeView, statusFilter, propertyTypeFilter]);
  
  const fetchRegistrations = async () => {
    try {
      setIsLoading(true);
      const data = await getAllRegistrations();
      setRegistrations(data);
    } catch (error) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: language === 'en' ? "Failed to load registrations" : "Échec du chargement des inscriptions",
        variant: "destructive"
      });
      
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminLoggedIn');
        navigate('/admin');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchMapMarkers = async () => {
    try {
      setIsLoadingMarkers(true);
      const filters = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (propertyTypeFilter !== 'all') filters.propertyType = propertyTypeFilter;
      
      const data = await getMapMarkers(filters);
      setMapMarkers(data);
    } catch (error) {
      console.error('Error fetching map markers:', error);
      setMapMarkers([]);
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: language === 'en' ? "Failed to load map data" : "Échec du chargement des données de la carte",
        variant: "destructive"
      });
    } finally {
      setIsLoadingMarkers(false);
    }
  };
  
  const fetchAdmins = async () => {
    try {
      setIsLoadingAdmins(true);
      const data = await getAllAdmins();
      setAdmins(data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: language === 'en' ? "Failed to load admin accounts" : "Échec du chargement des comptes administrateurs",
        variant: "destructive"
      });
    } finally {
      setIsLoadingAdmins(false);
    }
  };
  
  const handleCreateAdmin = async () => {
    if (newAdminPassword !== confirmPassword) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: t.passwordMismatch,
        variant: "destructive"
      });
      return;
    }
    
    if (newAdminPassword.length < 8) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: t.passwordTooShort,
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsCreatingAdmin(true);
      await createAdmin({ email: newAdminEmail, password: newAdminPassword });
      toast({
        title: language === 'en' ? "Success" : "Succès",
        description: t.adminCreated,
      });
      setShowCreateAdmin(false);
      setNewAdminEmail('');
      setNewAdminPassword('');
      setConfirmPassword('');
      fetchAdmins();
    } catch (error) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: error.response?.data?.detail || t.adminExists,
        variant: "destructive"
      });
    } finally {
      setIsCreatingAdmin(false);
    }
  };
  
  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm(language === 'en' ? 'Are you sure you want to delete this admin?' : 'Êtes-vous sûr de vouloir supprimer cet administrateur?')) {
      return;
    }
    
    try {
      setDeletingAdminId(adminId);
      await deleteAdmin(adminId);
      toast({
        title: language === 'en' ? "Success" : "Succès",
        description: t.adminDeleted,
      });
      fetchAdmins();
    } catch (error) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: error.response?.data?.detail || (language === 'en' ? "Failed to delete admin" : "Échec de la suppression"),
        variant: "destructive"
      });
    } finally {
      setDeletingAdminId(null);
    }
  };
  
  const handleChangePassword = async (adminId) => {
    if (newPasswordValue !== confirmNewPasswordValue) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: t.passwordMismatch,
        variant: "destructive"
      });
      return;
    }
    
    if (newPasswordValue.length < 8) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: t.passwordTooShort,
        variant: "destructive"
      });
      return;
    }
    
    try {
      await changeAdminPassword(adminId, newPasswordValue);
      toast({
        title: language === 'en' ? "Success" : "Succès",
        description: t.passwordChanged,
      });
      setChangingPasswordId(null);
      setNewPasswordValue('');
      setConfirmNewPasswordValue('');
    } catch (error) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: error.response?.data?.detail || (language === 'en' ? "Failed to change password" : "Échec du changement de mot de passe"),
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportRegistrations();
      toast({
        title: language === 'en' ? "Export Successful" : "Exportation Réussie",
        description: language === 'en' ? "Registrations exported successfully" : "Inscriptions exportées avec succès",
      });
    } catch (error) {
      toast({
        title: language === 'en' ? "Export Failed" : "Échec de l'exportation",
        description: language === 'en' ? "Failed to export registrations" : "Échec de l'exportation des inscriptions",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };
  
  const handleStatusUpdate = async (registrationId, newStatus) => {
    try {
      setUpdatingStatusId(registrationId);
      await updateRegistration(registrationId, { status: newStatus });
      
      // Update local state
      setRegistrations(prev => 
        prev.map(reg => 
          reg.id === registrationId ? { ...reg, status: newStatus } : reg
        )
      );
      
      toast({
        title: t.statusUpdated,
        description: language === 'en' 
          ? `Registration status updated to ${newStatus}` 
          : `Statut de l'inscription mis à jour: ${newStatus}`,
      });
    } catch (error) {
      toast({
        title: t.statusUpdateFailed,
        description: error.response?.data?.detail || (language === 'en' ? "An error occurred" : "Une erreur s'est produite"),
        variant: "destructive"
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPropertyTypeFilter('all');
    setMunicipalityFilter('all');
    setDetachmentFilter('all');
  };

  // Get unique municipalities from registrations
  const municipalities = useMemo(() => {
    const uniqueMunicipalities = [...new Set(
      registrations
        .filter(r => r.municipality)
        .map(r => r.municipality)
    )];
    return uniqueMunicipalities.sort();
  }, [registrations]);

  // Get unique detachments from registrations
  const detachments = useMemo(() => {
    const uniqueDetachments = [...new Set(
      registrations
        .filter(r => r.nearestDetachment)
        .map(r => r.nearestDetachment)
    )];
    return uniqueDetachments.sort();
  }, [registrations]);

  // Filter registrations
  const filteredRegistrations = useMemo(() => {
    return registrations.filter(r => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        r.firstName?.toLowerCase().includes(searchLower) ||
        r.lastName?.toLowerCase().includes(searchLower) ||
        r.email?.toLowerCase().includes(searchLower) ||
        r.city?.toLowerCase().includes(searchLower) ||
        r.municipality?.toLowerCase().includes(searchLower) ||
        r.nearestDetachment?.toLowerCase().includes(searchLower);
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      
      // Property type filter
      const matchesPropertyType = propertyTypeFilter === 'all' || r.propertyType === propertyTypeFilter;
      
      // Municipality filter
      const matchesMunicipality = municipalityFilter === 'all' || r.municipality === municipalityFilter;
      
      // Detachment filter
      const matchesDetachment = detachmentFilter === 'all' || r.nearestDetachment === detachmentFilter;
      
      return matchesSearch && matchesStatus && matchesPropertyType && matchesMunicipality && matchesDetachment;
    });
  }, [registrations, searchTerm, statusFilter, propertyTypeFilter, municipalityFilter, detachmentFilter]);

  const totalCameras = registrations.reduce((sum, r) => sum + parseInt(r.numberOfCameras || 0), 0);
  // Shared coordinate-validity rule (matches MapView): finite numbers within
  // geographic ranges. Keeps legitimate zero coordinates; rejects garbage.
  const hasValidCoords = (r) =>
    typeof r.latitude === 'number' && Number.isFinite(r.latitude) &&
    r.latitude >= -90 && r.latitude <= 90 &&
    typeof r.longitude === 'number' && Number.isFinite(r.longitude) &&
    r.longitude >= -180 && r.longitude <= 180;
  const registrationsWithLocation = registrations.filter(hasValidCoords).length;

  // Single source of truth for the Map tab: heading count, data passed to
  // MapView, and the empty state. Keeps the unrelated registration-summary
  // count above semantically separate.
  const validMapMarkers = useMemo(
    () => mapMarkers.filter(hasValidCoords),
    [mapMarkers]
  );
  
  const getStatusBadge = (status) => {
    const option = statusOptions.find(o => o.value === status) || statusOptions[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${option.color}`}>
        {option.label}
      </span>
    );
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || propertyTypeFilter !== 'all' || municipalityFilter !== 'all' || detachmentFilter !== 'all';

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t.dashboard}</h1>
                <p className="text-xs text-gray-600">Manitoba RCMP Admin</p>
              </div>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t.logout}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t.totalRegistrations}
              </CardTitle>
              <Users className="w-5 h-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{registrations.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t.totalCameras}
              </CardTitle>
              <Camera className="w-5 h-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{totalCameras}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                {t.registrationsWithLocation}
              </CardTitle>
              <MapPin className="w-5 h-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{registrationsWithLocation}</div>
            </CardContent>
          </Card>
        </div>

        {/* View Toggle Tabs */}
        <div className="flex items-center space-x-2 mb-6">
          <Button
            variant={activeView === 'table' ? 'default' : 'outline'}
            onClick={() => setActiveView('table')}
            className={activeView === 'table' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <List className="w-4 h-4 mr-2" />
            {t.tableView}
          </Button>
          <Button
            variant={activeView === 'map' ? 'default' : 'outline'}
            onClick={() => setActiveView('map')}
            className={activeView === 'map' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <MapPin className="w-4 h-4 mr-2" />
            {t.mapView}
          </Button>
          <Button
            variant={activeView === 'admins' ? 'default' : 'outline'}
            onClick={() => setActiveView('admins')}
            className={activeView === 'admins' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            <UserCog className="w-4 h-4 mr-2" />
            {t.adminManagement}
          </Button>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t.filterByStatus} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allStatuses}</SelectItem>
                {statusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Property Type Filter */}
            <Select value={propertyTypeFilter} onValueChange={setPropertyTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t.filterByPropertyType} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.allPropertyTypes}</SelectItem>
                {propertyTypeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Municipality Filter */}
            {municipalities.length > 0 && (
              <Select value={municipalityFilter} onValueChange={setMunicipalityFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t.filterByMunicipality} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allMunicipalities}</SelectItem>
                  {municipalities.map(muni => (
                    <SelectItem key={muni} value={muni}>
                      {muni}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Detachment Filter */}
            {detachments.length > 0 && (
              <Select value={detachmentFilter} onValueChange={setDetachmentFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder={t.filterByDetachment} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="all">{t.allDetachments}</SelectItem>
                  {detachments.map(det => (
                    <SelectItem key={det} value={det}>
                      {det}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-gray-500">
                <X className="w-4 h-4 mr-1" />
                {t.clearFilters}
              </Button>
            )}
            
            {/* Export Button */}
            <Button 
              onClick={handleExport}
              disabled={isExporting}
              className="bg-blue-600 hover:bg-blue-700 text-white ml-auto"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {t.exportData}
            </Button>
          </div>
        </div>

        {/* Content Area - Table or Map */}
        {activeView === 'table' ? (
          /* Table View */
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t.allRegistrations}</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchRegistrations}>
                <RefreshCw className="w-4 h-4 mr-1" />
                {language === 'en' ? 'Refresh' : 'Actualiser'}
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : filteredRegistrations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {t.noRegistrationsFound}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.name}</TableHead>
                        <TableHead>{t.contact}</TableHead>
                        <TableHead>{t.location}</TableHead>
                        <TableHead>{t.cameras}</TableHead>
                        <TableHead>{t.status}</TableHead>
                        <TableHead className="text-right">{t.actions}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRegistrations.map((reg) => (
                        <TableRow key={reg.id}>
                          <TableCell className="font-medium">
                            {reg.firstName} {reg.lastName}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{reg.email}</div>
                              <div className="text-gray-500">{reg.phone}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{reg.city}, {reg.province}</div>
                              {reg.municipality && (
                                <div className="text-gray-500">{reg.municipality}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{reg.numberOfCameras}</TableCell>
                          <TableCell>
                            <Select
                              value={reg.status || 'new'}
                              onValueChange={(value) => handleStatusUpdate(reg.id, value)}
                              disabled={updatingStatusId === reg.id}
                            >
                              <SelectTrigger className="w-[130px] h-8">
                                {updatingStatusId === reg.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  getStatusBadge(reg.status || 'new')
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(option => (
                                  <SelectItem key={option.value} value={option.value}>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${option.color}`}>
                                      {option.label}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDetailsFor(reg)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {t.viewDetails}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : activeView === 'map' ? (
          /* Map View */
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t.mapView}</CardTitle>
              <div className="text-sm text-gray-500">
                {isLoadingMarkers ? (
                  <Loader2 className="w-4 h-4 animate-spin inline" />
                ) : (
                  <>{validMapMarkers.length} {language === 'en' ? 'locations' : 'emplacements'}</>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingMarkers ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : validMapMarkers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {language === 'en' 
                    ? 'No registrations with location data available' 
                    : 'Aucune inscription avec données de localisation disponible'}
                </div>
              ) : (
                <MapView
                  markers={validMapMarkers}
                  onViewDetails={(marker) => {
                    // Marker ids and registration ids are both the stringified
                    // MongoDB _id in their respective API responses, so this
                    // resolves the exact record. If the full registration is
                    // not loaded, show a clear message rather than nothing or
                    // the wrong record. The dialog opens in both cases.
                    openDetailsFor(registrations.find(r => r.id === marker.id) || null);
                  }}
                />
              )}
            </CardContent>
          </Card>
        ) : activeView === 'admins' ? (
          /* Admin Management View */
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t.adminAccounts}</CardTitle>
              <div className="flex space-x-2">
                <Button variant="ghost" size="sm" onClick={fetchAdmins}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  {language === 'en' ? 'Refresh' : 'Actualiser'}
                </Button>
                <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      <UserPlus className="w-4 h-4 mr-2" />
                      {t.addAdmin}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t.createNewAdmin}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <label className="text-sm font-medium">{t.adminEmail}</label>
                        <Input
                          type="email"
                          value={newAdminEmail}
                          onChange={(e) => setNewAdminEmail(e.target.value)}
                          placeholder="admin@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t.adminPassword}</label>
                        <Input
                          type="password"
                          value={newAdminPassword}
                          onChange={(e) => setNewAdminPassword(e.target.value)}
                          placeholder="••••••••"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t.confirmAdminPassword}</label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••"
                          className="mt-1"
                        />
                      </div>
                      <Button 
                        onClick={handleCreateAdmin}
                        disabled={isCreatingAdmin || !newAdminEmail || !newAdminPassword}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        {isCreatingAdmin ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        {t.createAdmin}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingAdmins ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : admins.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {t.noAdminsFound}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.adminEmail}</TableHead>
                        <TableHead>{t.role}</TableHead>
                        <TableHead>{t.createdDate}</TableHead>
                        <TableHead className="text-right">{t.actions}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {admins.map((admin) => (
                        <TableRow key={admin.id}>
                          <TableCell className="font-medium">{admin.email}</TableCell>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 capitalize">
                              {admin.role}
                            </span>
                          </TableCell>
                          <TableCell>
                            {admin.createdDate ? new Date(admin.createdDate).toLocaleDateString(language === 'en' ? 'en-CA' : 'fr-CA') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              {/* Change Password Dialog */}
                              <Dialog open={changingPasswordId === admin.id} onOpenChange={(open) => {
                                if (!open) {
                                  setChangingPasswordId(null);
                                  setNewPasswordValue('');
                                  setConfirmNewPasswordValue('');
                                }
                              }}>
                                <DialogTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setChangingPasswordId(admin.id)}
                                  >
                                    <Key className="w-4 h-4 mr-1" />
                                    {t.changePassword}
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>{t.changePassword} - {admin.email}</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div>
                                      <label className="text-sm font-medium">{t.newPassword}</label>
                                      <Input
                                        type="password"
                                        value={newPasswordValue}
                                        onChange={(e) => setNewPasswordValue(e.target.value)}
                                        placeholder="••••••••"
                                        className="mt-1"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium">{t.confirmNewPassword}</label>
                                      <Input
                                        type="password"
                                        value={confirmNewPasswordValue}
                                        onChange={(e) => setConfirmNewPasswordValue(e.target.value)}
                                        placeholder="••••••••"
                                        className="mt-1"
                                      />
                                    </div>
                                    <Button 
                                      onClick={() => handleChangePassword(admin.id)}
                                      disabled={!newPasswordValue || !confirmNewPasswordValue}
                                      className="w-full bg-blue-600 hover:bg-blue-700"
                                    >
                                      <Key className="w-4 h-4 mr-2" />
                                      {t.updatePassword}
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                              
                              {/* Delete Button */}
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-600 border-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteAdmin(admin.id)}
                                disabled={deletingAdminId === admin.id}
                              >
                                {deletingAdminId === admin.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}

      {/* Single shared details dialog for the Registrations table and the
          Map tab's "View details" action. Rendered outside the active-view
          switch so it is available on any tab. Open state (detailsOpen) is
          independent of the selected record so an unmatched marker can show
          the unavailable-record message. */}
      <Dialog open={detailsOpen} onOpenChange={(open) => {
        if (!open) {
          closeDetails();
        }
      }}>
        <DialogContent className="max-w-2xl">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {selectedUser.firstName} {selectedUser.lastName} - {t.viewDetails}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.emailAddress}</p>
                    <p className="text-sm">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.phone}</p>
                    <p className="text-sm">{selectedUser.phone}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">{t.streetAddress}</p>
                  <p className="text-sm">{selectedUser.streetAddress}, {selectedUser.city}, {selectedUser.province} {selectedUser.postalCode}</p>
                </div>
                {selectedUser.municipality && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.municipality}</p>
                    <p className="text-sm">{selectedUser.municipality}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.propertyType}</p>
                    <p className="text-sm capitalize">{selectedUser.propertyType || 'residential'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.status}</p>
                    {getStatusBadge(selectedUser.status || 'new')}
                  </div>
                </div>
                {selectedUser.nearestDetachment && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.nearestDetachment}</p>
                    <p className="text-sm">{selectedUser.nearestDetachment}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.numberOfCameras}</p>
                    <p className="text-sm">{selectedUser.numberOfCameras}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600">{t.cameraBrand}</p>
                    <p className="text-sm">{selectedUser.cameraBrand}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">{t.cameraLocation}</p>
                  <p className="text-sm">{selectedUser.cameraLocation}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">{t.coverageArea}</p>
                  <p className="text-sm">{selectedUser.coverageArea}</p>
                </div>
                {selectedUser.latitude != null && selectedUser.longitude != null && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Latitude</p>
                      <p className="text-sm">{formatCoord(selectedUser.latitude)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Longitude</p>
                      <p className="text-sm">{formatCoord(selectedUser.longitude)}</p>
                    </div>
                  </div>
                )}

                {/* Edit Coordinates Section */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {language === 'en' ? 'Edit Map Location' : 'Modifier l\'emplacement sur la carte'}
                  </p>
                  {!selectedUser.latitude && !selectedUser.longitude && (
                    <p className="text-xs text-orange-600 mb-2">
                      {language === 'en'
                        ? '⚠️ No coordinates set - this user won\'t appear on the map'
                        : '⚠️ Aucune coordonnée définie - cet utilisateur n\'apparaîtra pas sur la carte'}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500">Latitude</label>
                      <Input
                        type="number"
                        step="0.000001"
                        placeholder="e.g. 49.8951"
                        value={latDraft}
                        onChange={(e) => setLatDraft(e.target.value)}
                        disabled={isSavingCoords}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Longitude</label>
                      <Input
                        type="number"
                        step="0.000001"
                        placeholder="e.g. -97.1384"
                        value={lngDraft}
                        onChange={(e) => setLngDraft(e.target.value)}
                        disabled={isSavingCoords}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {language === 'en'
                      ? 'Tip: Find coordinates on Google Maps by right-clicking a location'
                      : 'Astuce: Trouvez les coordonnées sur Google Maps en faisant un clic droit sur un emplacement'}
                  </p>
                  <Button
                    size="sm"
                    className="mt-2 bg-blue-600 hover:bg-blue-700"
                    disabled={isSavingCoords}
                    onClick={async () => {
                      // Synchronous guard: check and set the ref before any
                      // await so repeated calls send only one PATCH. React
                      // saving state is separate for rendering.
                      if (saveInFlightRef.current) return;
                      const targetId = openRecordIdRef.current;
                      if (!targetId) return;
                      const session = detailsSessionRef.current;

                      const latText = latDraft.trim();
                      const lngText = lngDraft.trim();
                      if (latText === '' || lngText === '') {
                        toast({
                          title: language === 'en' ? "Invalid Coordinates" : "Coordonnées invalides",
                          description: language === 'en'
                            ? "Please enter valid latitude and longitude values"
                            : "Veuillez entrer des valeurs de latitude et longitude valides",
                          variant: "destructive"
                        });
                        return;
                      }

                      const newLat = Number(latText);
                      const newLng = Number(lngText);
                      if (!Number.isFinite(newLat) || !Number.isFinite(newLng) || newLat < -90 || newLat > 90 || newLng < -180 || newLng > 180) {
                        toast({
                          title: language === 'en' ? "Invalid Coordinates" : "Coordonnées invalides",
                          description: language === 'en'
                            ? "Latitude must be between -90 and 90 and longitude between -180 and 180"
                            : "La latitude doit être entre -90 et 90 et la longitude entre -180 et 180",
                          variant: "destructive"
                        });
                        return;
                      }

                      saveInFlightRef.current = true;
                      setIsSavingCoords(true);
                      try {
                        await updateRegistration(targetId, {
                          latitude: newLat,
                          longitude: newLng
                        });
                        toast({
                          title: language === 'en' ? "Coordinates Updated" : "Coordonnées mises à jour",
                          description: language === 'en'
                            ? "Map location has been updated successfully"
                            : "L'emplacement sur la carte a été mis à jour avec succès",
                        });
                        fetchRegistrations();
                        if (activeView === 'map') {
                          fetchMapMarkers();
                        }
                        // Response-time guard: apply the result only if the
                        // same record is still open in the same session.
                        if (detailsSessionRef.current === session && openRecordIdRef.current === targetId) {
                          setSelectedUser(prev => prev && prev.id === targetId ? { ...prev, latitude: newLat, longitude: newLng } : prev);
                          setLatDraft(formatCoord(newLat));
                          setLngDraft(formatCoord(newLng));
                        }
                      } catch (error) {
                        toast({
                          title: language === 'en' ? "Update Failed" : "Échec de la mise à jour",
                          description: error.message,
                          variant: "destructive"
                        });
                        // Draft is retained so the admin can correct/retry.
                      } finally {
                        saveInFlightRef.current = false;
                        setIsSavingCoords(false);
                      }
                    }}
                  >
                    <MapPin className="w-4 h-4 mr-1" />
                    {isSavingCoords
                      ? (language === 'en' ? 'Saving…' : 'Enregistrement…')
                      : (language === 'en' ? 'Save Coordinates' : 'Enregistrer les coordonnées')}
                  </Button>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-600">{t.registeredOn}</p>
                  <p className="text-sm">{selectedUser.registeredDate ? new Date(selectedUser.registeredDate).toLocaleString(language === 'en' ? 'en-CA' : 'fr-CA') : '-'}</p>
                </div>
              </div>
            </>
          )}
          {mapDetailsNotFound && !selectedUser && (
            <div className="text-center py-6 text-gray-600">
              {language === 'en'
                ? 'The full registration details for this map marker are not loaded yet. Please return to the Registrations tab and try again, or reload the dashboard.'
                : "Les détails complets de l'inscription pour ce marqueur ne sont pas encore chargés. Veuillez retourner à l'onglet Inscriptions et réessayer, ou recharger le tableau de bord."}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
